// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import toggleLeaseAgreement from "@guestsupply/App/Handlers/XNG/CartLeaseAgreement";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    cart?: Cart;
    extendedStyles?: CartShippingSummaryDisplayStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    isGuest: getSession(state).isGuest,
    shipTo: getShipToState(state, getSession(state).shipToId)?.value,
    siteName: state.context.website.name,
});
const mapDispatchToProps = {
    toggleLeaseAgreement,
};
type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CartShippingSummaryDisplayStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    // XNG-CHANGES
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    wrapper?: InjectableCss;
    tooltipStyles?: TooltipPresentationProps;
    freeShippingStyle?: TypographyProps;
}

export const cartShippingSummaryDisplayStyles: CartShippingSummaryDisplayStyles = {};

const CartShippingSummaryDisplay = ({
    isCartEmpty = false,
    cart,
    extendedStyles,
    isAuthenticated,
    isGuest,
    shipTo,
    siteName,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartShippingSummaryDisplayStyles, extendedStyles));
    const stockItemsTotal = React.useMemo(() => {
        let stockTotal = 0;
        cart?.cartLines?.forEach(line => {
            if (line.availability) {
                const { messageType, message } = line.availability;
                const isInStockUS = messageType === 1 && ["In Stock", "Special Order"].includes(message) && siteName?.includes("US");
                const isInStock = messageType === 1 && message === "In Stock";
                const isOtherType = messageType === 2 || messageType === 3;

                if (isInStock || isInStockUS || isOtherType) {
                    if (line.pricing) {
                        stockTotal += line.pricing.extendedUnitNetPrice;
                    }
                }
            }
        });
        return stockTotal;
    }, [cart?.cartLines]);

    if (!cart || isCartEmpty) {
        return null;
    }
    const freightAmount = shipTo?.properties.freightAmount ?? "0";
    const freeShippingOrderTotal = parseInt(freightAmount, 10);
    const remainingTotalToFreeShipping = freeShippingOrderTotal - stockItemsTotal;
    const hasDropShipment =
        cart?.cartLines?.some(
            (line: CartLineModel) =>
                line?.availability &&
                line?.availability["messageType"] === 1 &&
                line.availability["message"] === "Drop Shipment",
        ) || false;

    return (
        <StyledWrapper {...styles.wrapper}>
            {isAuthenticated && !isGuest && (
                <GridContainer
                    {...styles.container}
                    data-test-selector={`shippingSummary_${cart.id}`}
                    className="shipping-summary"
                >
                    <GridItem {...styles.titleGridItem}>
                        <Typography {...styles.titleText}>{translate("Shipping Summary")}</Typography>
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.subtotalLabel}>
                            <Typography {...styles.freeShippingStyle}>
                                {translate("Free Shipping Threshold")}
                            </Typography>
                            <Tooltip
                                {...styles.tooltipStyles}
                                text={translate(
                                    "Free shipping is valid only on stocked items. Drop shipped items do not apply to the minimum and will be billed freight.",
                                )}
                            />
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography
                            {...styles.subtotalValue}
                            data-test-selector="shippingSummary_FreeShippingThreshold"
                        >
                            {freeShippingOrderTotal <= 0 ? (
                                translate("N/A")
                            ) : (
                                <LocalizedCurrency
                                    amount={freeShippingOrderTotal}
                                    currencySymbol={cart.currencySymbol}
                                />
                            )}
                        </Typography>
                    </GridItem>
                    {remainingTotalToFreeShipping <= 0 && !hasDropShipment && (
                        <GridItem width={12}>
                            <Typography
                                {...styles.subtotalLabel}
                                data-test-selector="shippingSummary_remainingTotalToFreeShipping"
                            >
                                {translate("Your order qualifies for Free Shipping!")}
                            </Typography>
                        </GridItem>
                    )}
                    {remainingTotalToFreeShipping <= 0 && hasDropShipment && (
                        <GridItem width={12}>
                            <Typography
                                {...styles.subtotalLabel}
                                data-test-selector="shippingSummary_remainingTotalToFreeShipping"
                            >
                                {siteMessage(
                                    "Cart_Drop_Ship_Items",
                                    "You are receiving free shipping on eligible items!",
                                )}
                            </Typography>
                        </GridItem>
                    )}
                    {remainingTotalToFreeShipping > 0 && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>
                                    {translate("Total needed to reach Free Shipping")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.subtotalValue}
                                    data-test-selector="shippingSummary_remainingTotalToFreeShipping"
                                >
                                    <LocalizedCurrency
                                        amount={remainingTotalToFreeShipping}
                                        currencySymbol={cart.currencySymbol}
                                    />
                                </Typography>
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CartShippingSummaryDisplay);
